// Historia 120 — Bloque 13 (Jupiter, paquete P7): `features/S143-detalle-y-acciones-de-lote-jupiter.feature`
// (S143-S151) + `features/S206-preparacion-y-reporte-jupiter.feature` (S207).
//
// The master-detail widget (`ActiveMultiJobContainer.jsx`) offers EXACTLY TWO batch actions --
// *Pause batch* and *Finish batch* -- and ONLY for the active jobs that share a `batchId` (F3,
// `common/reportOutcome.js:getJobBatchId`). ⚠️ Enmienda 02 (G3-4/RN37.1) already retired the third
// action (*Report all jobs*): this module never grows a third one, and no multi-job report dialog
// is built here or anywhere else (S207) -- reporting stays the per-job flow the container already
// has (`submitOkReport`/`handleReportOk`, unchanged).
//
// RN67/POL1: the batch ADDS a gesture, it never replaces or hides the individual actions --
// `resolveJobModeActions` (per selected job) is computed independently, exactly as before this
// story. S148/POL3: a PRE-EXISTING gate (acceptance form, external circuit) still governs; this hook
// only DISABLES what those gates already disable, it never re-enables anything.
//
// S149/D-I11/§8.4: there is no "abandon"/"cancel the batch" action here, and pausing IS parking +
// unloading + pausing as ONE gesture (N3) -- there is only ever one pause action per batch group.
// S150/CA4.4: there is no "edit the batch composition" affordance -- changing who is in the batch is
// pause (this hook) + start again in batch (a different, already-built package), never an edit here.
import { useCallback, useMemo, useState } from 'react'
import { pauseBatch } from './batchApi'
import { buildBatchPauseRequest } from './batchContract'
import { translateBatchRejectionMessage } from './batchReasons'

const EMPTY_BLOCKED_PAUSE = { open: false, jobs: [] }

const jobRefOf = (item) => ({
  fabricationOrderId: item?.fabricationOrder?.id,
  jobReferenceId: item?.jobReference?.id
})

// S148: the SAME two pre-existing gates the individual actions already read off the active item
// (`getJobGovernedByAcceptance`/`getJobExternalCircuit` in `reportOutcome.js`), read here straight
// off the same item -- no new gate, no new field, same precedence (acceptance first) the container
// already applies to the single selected job.
const gateOf = (item, t_) => {
  if (item?.governedByAcceptance === true) {
    return t_('Acceptance job: actions are governed by the acceptance form')
  }
  if (item?.externalCircuitJob === true) {
    return t_('External job: actions are governed by the external jobs widget')
  }
  return ''
}

/**
 * S144/S145 -- groups the active items by their (non-empty) `batchId`. Every distinct batch present
 * among today's active items gets its OWN entry.
 *
 * ⚠️ **ENMIENDA 06 (G3-10): esta agrupacion sigue siendo la derivacion, pero YA NO ES LO QUE SE PINTA.**
 * Lo que se ofrece es UNA sola pareja, la del lote del job SELECCIONADO (`selectedBatchGroup`). Pintar
 * una fila de botones por grupo era el defecto que el PO vio: con dos lotes activos, dos filas
 * identicas e indistinguibles.
 *
 * 🔒 F3/§8.4 -- la composicion la deriva Sun: cada grupo se arma con el `batchId` que cada job activo
 * YA publica. `fabricationOrderCodes` sale de esos mismos jobs, sin ninguna peticion adicional y sin
 * pedirle a Sun ningun campo nuevo (S238).
 *
 * @param {Array<Object>} activeItems
 * @param {function(string): string} t_
 * @returns {Array<{batchId: string, jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>, fabricationOrderCodes: Array<string>, disabled: boolean, disabledTooltip: string}>}
 *
 * ⚠️ **ENMIENDA 08 (G3-12 b): se exporta** porque los OTROS DOS widgets que listan jobs activos
 * («Active jobs» y «Operation») necesitan los MISMOS grupos para pintar el distintivo de lote, y
 * repetir la agrupación en cada uno sería la forma más rápida de que los tres dejaran de coincidir.
 */
export const groupByBatchId = (activeItems, t_) => {
  const list = Array.isArray(activeItems) ? activeItems : []
  const groups = new Map()

  list.forEach((item) => {
    const batchId = item?.batchId || ''
    if (!batchId) return

    const tooltip = gateOf(item, t_)
    const code = item?.fabricationOrder?.fabricationOrder || ''
    const existing = groups.get(batchId)
    if (!existing) {
      groups.set(batchId, {
        batchId,
        jobs: [jobRefOf(item)],
        fabricationOrderCodes: code ? [code] : [],
        disabled: Boolean(tooltip),
        disabledTooltip: tooltip
      })
      return
    }
    existing.jobs.push(jobRefOf(item))
    if (code && !existing.fabricationOrderCodes.includes(code)) {
      existing.fabricationOrderCodes.push(code)
    }
    // POL3: one gated job is enough to withhold the BATCH action -- the gate never gets diluted by
    // the other members' actions being available (S148: "the batch does not enable what the gate
    // disables").
    if (tooltip && !existing.disabled) {
      existing.disabled = true
      existing.disabledTooltip = tooltip
    }
  })

  return Array.from(groups.values())
}

/**
 * Historia 120 (Jupiter, paquete P7) -- pause/finish-batch actions for the active multi-job
 * master-detail. Only N3 (pause) is called directly here: *Finish batch* is a trigger that opens the
 * already-built `BatchFinishDialog` (N4+N5, paquete P8) with the right `batchId` -- this hook does not
 * duplicate its preview/BLOCKED/CONFIRMATION_REQUIRED handling.
 *
 * @param {Object} params
 * @param {Array<Object>} params.activeItems Same shape as `useActiveMultiJob().activeItems`.
 * @param {{id: number}} [params.device]
 * @param {{id: number}} [params.worker]
 * @param {{fabricationOrderId: number, jobReferenceId: number}} [params.selectedJob] The job selected
 *   in the master-detail. ENMIENDA 06 (G3-10): the pair of batch actions offered is ITS batch's, and
 *   none is offered when it has no batch.

 * @param {function(): void} [params.refreshActiveJobs] Called after a successful pause so the poll
 *   reflects the paused jobs leaving the active list (RN29: quantities/time are a server guarantee,
 *   not something this hook computes).
 * @param {function({severity: string, message: string}): void} [params.showAlert]
 * @param {function(string, Object=): string} [params.t_]
 * @returns {{
 *   batchGroups: Array<{batchId: string, jobs: Array, fabricationOrderCodes: Array<string>, disabled: boolean, disabledTooltip: string}>,
 *   selectedBatchGroup: Object|null,
 *   pauseBatchGroup: function(string): Promise<void>,
 *   blockedPause: {open: boolean, jobs: Array},
 *   closeBlockedPause: function(): void,
 *   finishBatchId: string,
 *   openFinishBatch: function(string): void,
 *   closeFinishBatch: function(): void,
 *   handleBatchFinished: function(): void
 * }}
 */
export const useBatchActions = ({
  activeItems = [],
  device,
  worker,
  selectedJob = {},
  refreshActiveJobs = () => {},
  showAlert = () => {},
  t_ = (key) => key
} = {}) => {
  const [blockedPause, setBlockedPause] = useState(EMPTY_BLOCKED_PAUSE)
  const [finishBatchId, setFinishBatchId] = useState('')

  const batchGroups = useMemo(() => groupByBatchId(activeItems, t_), [activeItems, t_])

  // 🔴 S144/S145/S237 (ENMIENDA 06) -- EL LOTE DEL JOB SELECCIONADO, y solo el. Si el job seleccionado
  // no tiene lote, esto es `null` y el contenedor no pinta ninguna accion de lote, aunque en la maquina
  // haya otros jobs que si lo tengan: dependen del seleccionado y solo de el.
  const selectedBatchId = useMemo(() => {
    const list = Array.isArray(activeItems) ? activeItems : []
    const match = list.find((item) =>
      item?.fabricationOrder?.id === selectedJob?.fabricationOrderId &&
      item?.jobReference?.id === selectedJob?.jobReferenceId)
    return match?.batchId || ''
  }, [activeItems, selectedJob?.fabricationOrderId, selectedJob?.jobReferenceId])

  const selectedBatchGroup = useMemo(
    () => batchGroups.find((group) => group.batchId === selectedBatchId) ?? null,
    [batchGroups, selectedBatchId])

  // S147: closing the notice needs no cleanup -- N3's BLOCKED writes nothing (D-SP14/D-I4), so the
  // operator can fire the exact same gesture again right away.
  const closeBlockedPause = useCallback(() => setBlockedPause(EMPTY_BLOCKED_PAUSE), [])

  const pauseBatchGroup = useCallback(async (batchId) => {
    const group = batchGroups.find((candidate) => candidate.batchId === batchId)
    if (!group) return

    const request = buildBatchPauseRequest({
      deviceId: device?.id,
      workerId: worker?.id,
      batchId,
      jobs: group.jobs
    })

    try {
      const response = await pauseBatch(request)
      if (response?.type === 'BLOCKED') {
        // D-SP14/D-I4: a 200 that writes nothing -- none of the jobs change state on screen, so
        // `refreshActiveJobs` is deliberately NOT called.
        setBlockedPause({ open: true, jobs: Array.isArray(response.jobs) ? response.jobs : [] })
        return
      }
      setBlockedPause(EMPTY_BLOCKED_PAUSE)
      refreshActiveJobs()
    } catch (error) {
      // The gesture-wide 400 rejections (BatchNoLongerActive, BatchSelectionEmpty,
      // BatchDuplicateJob) never present as a raw network/system error -- same single translator
      // every other batch surface uses (§7.2 #15-ter, no whitelist).
      showAlert({ severity: 'error', message: translateBatchRejectionMessage(t_, error) })
    }
  }, [batchGroups, device, worker, refreshActiveJobs, showAlert, t_])

  const openFinishBatch = useCallback((batchId) => setFinishBatchId(batchId), [])
  const closeFinishBatch = useCallback(() => setFinishBatchId(''), [])
  const handleBatchFinished = useCallback(() => {
    setFinishBatchId('')
    refreshActiveJobs()
  }, [refreshActiveJobs])

  return {
    batchGroups,
    selectedBatchGroup,
    pauseBatchGroup,
    blockedPause,
    closeBlockedPause,
    finishBatchId,
    openFinishBatch,
    closeFinishBatch,
    handleBatchFinished
  }
}

export default useBatchActions
